import React, { useState } from "react";
import { api, ApiError } from "../lib/api.js";
import { IcSearch, IcArrow, IcMail, IcCheck } from "./shared.jsx";
import { useIsMobile } from "../lib/useIsMobile.js";

// ============ FIND YOU — entry-point identity + email-verify gate (§2) ============
//
// The single shared QR lands here. The agent identifies themselves; we look up
// their record server-side and email a single-use confirmation LINK to the
// address ON FILE (they never type an email). Following that link (handled by the
// /continue route) mints the session and resumes the wizard at step 1. This
// component never advances the wizard itself — auth completes out-of-band via the
// emailed link.
//
// Flow: idle → (Find Me) → found{agentRef,emailHint} → (Send link) → sent
//       └─ nomatch on 404.

export function StepSearch() {
  const [phase, setPhase] = useState("idle");   // idle | searching | found | sending | sent | nomatch | email_unavailable
  const [q, setQ] = useState("");
  const [field, setField] = useState("recruit"); // email-based recruit lookup is the only entry path (MLS/license search removed 2026-07-20)
  const [match, setMatch] = useState(null);       // { agentRef, emailHint }
  const [error, setError] = useState(null);

  async function doSearch() {
    if (!q.trim()) return;
    setPhase("searching");
    setError(null);
    try {
      const res = field === "recruit"
        ? await api.post("/v1/onboard/identify-recruit", { email: q.trim() })
        : await api.post("/v1/onboard/identify", { query: q.trim(), field });
      // Found in the MLS but ARMLS shared no email → can't send a verify link.
      if (res?.emailUnavailable) { setMatch(null); setPhase("email_unavailable"); return; }
      setMatch(res);
      setPhase("found");
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setPhase("nomatch");
      else { setError(err.message || "Lookup failed"); setPhase("idle"); }
    }
  }

  async function sendLink(resend = false) {
    if (!match?.agentRef) return;
    setPhase("sending");
    setError(null);
    try {
      await api.post(resend ? "/v1/onboard/resend-link" : "/v1/onboard/send-link", { agentRef: match.agentRef });
      setPhase("sent");
    } catch (err) {
      // Throttle (429) is a soft state — we still moved to "sent" on the first
      // send; on resend show the throttle message but stay on the sent screen.
      if (err instanceof ApiError && err.status === 429) { setPhase("sent"); setError(err.message); }
      else { setError(err.message || "Could not send the link"); setPhase("found"); }
    }
  }

  function reset() { setPhase("idle"); setQ(""); setMatch(null); setError(null); }

  return (
    <div style={{ maxWidth: 880 }}>
      {phase === "found" || phase === "sending" ? (
        <ConfirmSend match={match} sending={phase === "sending"} error={error} onSend={() => sendLink(false)} onReset={reset} />
      ) : phase === "sent" ? (
        <SentView match={match} error={error} onResend={() => sendLink(true)} onReset={reset} />
      ) : phase === "email_unavailable" ? (
        <EmailUnavailable onReset={reset} />
      ) : (
        <SearchHero phase={phase} q={q} setQ={setQ} field={field} setField={setField}
          onSearch={doSearch} onReset={reset} error={error} />
      )}
    </div>
  );
}

// ============ SEARCH HERO ============
function SearchHero({ phase, q, setQ, field, setField, onSearch, onReset, error }) {
  const isMobile = useIsMobile();
  const phs = {
    recruit: "Enter the email you gave the team",
  };

  return (
    <div>
      <div className="ep-eyebrow" style={{ color: "var(--ep-sunset)" }}>Welcome Aboard</div>
      <h1 style={{ font: "300 clamp(48px, 6.5vw, 84px)/1 var(--ep-font-display)", letterSpacing: "-0.02em", margin: "12px 0 24px" }}>
        Let's find you.
      </h1>
      <p style={{ font: "400 19px/1.55 var(--ep-font-body)", color: "var(--ep-fg-muted)", maxWidth: "54ch", marginBottom: 40 }}>
        Your onboarding invite was emailed to you when the team submitted your intake — the fastest way
        in is that link. Lost it? Enter the email you gave the team and we'll send you a fresh one.
      </p>

      <div style={{ background: "var(--ep-bg-elevated)", border: "1px solid var(--ep-border)", padding: "28px 28px 24px", marginBottom: 32 }}>
        <form onSubmit={(e) => { e.preventDefault(); onSearch(); }} style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 12, alignItems: "stretch" }}>
          <div style={{ flex: 1, position: "relative" }}>
            <div style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--ep-fg-muted)" }}>
              <IcSearch size={18} />
            </div>
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={phs[field]}
              disabled={phase === "searching"}
              style={{
                width: "100%", padding: "16px 16px 16px 44px", fontSize: 17,
                fontFamily: "var(--ep-font-body)", border: "1px solid var(--ep-border)",
                background: "var(--ep-saguaro-blossom)", color: "var(--ep-origin)",
                borderRadius: 2, boxSizing: "border-box", outline: "none",
              }}
            />
          </div>
          <button type="submit" className="ep-btn primary" disabled={!q.trim() || phase === "searching"}
            style={{ padding: "0 28px", width: isMobile ? "100%" : undefined, justifyContent: isMobile ? "center" : undefined }}>
            {phase === "searching" ? <>Searching…</> : <>Find Me <IcArrow /></>}
          </button>
        </form>

        {error && (
          <div style={{ marginTop: 14, fontSize: 13, color: "var(--ep-rosewood)" }}>{error}</div>
        )}

        {phase === "nomatch" && (
          <div style={{
            marginTop: 18, padding: "14px 18px", background: "#FDF2E9", border: "1px solid var(--ep-sunset)",
            display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16,
          }}>
            <div>
              <div style={{ font: "500 14px/1.3 var(--ep-font-body)", color: "var(--ep-origin)" }}>No match for "{q}"</div>
              <div style={{ fontSize: 12, color: "var(--ep-fg-muted)", marginTop: 2 }}>Double-check the email you gave the team, or reach out so we can confirm your intake was submitted.</div>
            </div>
            <button className="ep-btn ghost sm" onClick={onReset}>Try again</button>
          </div>
        )}
      </div>

      <p style={{ fontSize: 12, color: "var(--ep-fg-muted)", maxWidth: "60ch" }}>
        Can't find yourself? Reach out to <a href="mailto:onboard@everyplace.com" style={{ color: "var(--ep-sunset)" }}>onboard@everyplace.com</a> — we may need to add your record before you can continue.
      </p>
    </div>
  );
}

// ============ CONFIRM + SEND LINK ============
function ConfirmSend({ match, sending, error, onSend, onReset }) {
  const isMobile = useIsMobile();
  return (
    <div>
      <div className="ep-eyebrow" style={{ color: "var(--ep-sunset)" }}>Match Found</div>
      <h1 style={{ font: "300 clamp(40px, 5vw, 60px)/1.02 var(--ep-font-display)", letterSpacing: "-0.02em", margin: "10px 0 14px" }}>
        That's you?
      </h1>
      <p style={{ font: "400 17px/1.55 var(--ep-font-body)", color: "var(--ep-fg-muted)", maxWidth: "56ch", marginBottom: 28 }}>
        We found your record. To confirm it's really you, we'll email a secure link to the
        address on file. For your security we only show a hint of it here.
      </p>

      <div style={{
        display: "flex", flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "stretch" : "center", gap: isMobile ? 16 : 24, padding: "22px 28px",
        background: "var(--ep-origin)", color: "var(--ep-saguaro-blossom)", borderRadius: 2,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="ep-eyebrow" style={{ color: "var(--ep-high-noon)", marginBottom: 6 }}>Confirmation link will go to</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, font: "400 22px/1.2 var(--ep-font-body)", overflowWrap: "anywhere" }}>
            <IcMail size={18} /> {match?.emailHint}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 10 : 14,
          alignItems: "stretch", flexShrink: 0 }}>
          <button className="ep-btn primary" onClick={onSend} disabled={sending}
            style={{ fontSize: 13, padding: "16px 30px", justifyContent: "center", order: isMobile ? 1 : 2 }}>
            {sending ? "Sending…" : <>Send my link <IcArrow size={16} /></>}
          </button>
          <button className="ep-btn ghost" onClick={onReset}
            style={{ color: "var(--ep-saguaro-blossom)", fontSize: 12, padding: "16px 22px", justifyContent: "center", order: isMobile ? 2 : 1 }}>Not me</button>
        </div>
      </div>
      {error && <div style={{ marginTop: 14, fontSize: 13, color: "var(--ep-rosewood)" }}>{error}</div>}
    </div>
  );
}

// ============ EMAIL UNAVAILABLE — found, but no ARMLS email on file ============
function EmailUnavailable({ onReset }) {
  return (
    <div>
      <div className="ep-eyebrow" style={{ color: "var(--ep-sunset)" }}>We found you</div>
      <h1 style={{ font: "300 clamp(40px, 5vw, 60px)/1.02 var(--ep-font-display)", letterSpacing: "-0.02em", margin: "10px 0 14px" }}>
        Almost there.
      </h1>
      <p style={{ font: "400 17px/1.55 var(--ep-font-body)", color: "var(--ep-fg-muted)", maxWidth: "58ch", marginBottom: 24 }}>
        We matched your MLS record, but ARMLS hasn't shared an email address for it — so we can't send
        your secure confirmation link automatically. Reach out and we'll get you verified.
      </p>
      <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
        <a className="ep-btn primary" href="mailto:onboard@everyplace.com?subject=Onboarding%20%E2%80%94%20no%20email%20on%20file"
           style={{ textDecoration: "none" }}>
          Contact onboarding
        </a>
        <button className="ep-btn ghost sm" onClick={onReset}>Try a different search</button>
      </div>
    </div>
  );
}

// ============ SENT — check your inbox ============
function SentView({ match, error, onResend, onReset }) {
  return (
    <div>
      <div className="ep-eyebrow" style={{ color: "var(--ep-sunset)" }}>Check your inbox</div>
      <h1 style={{ font: "300 clamp(40px, 5vw, 60px)/1.02 var(--ep-font-display)", letterSpacing: "-0.02em", margin: "10px 0 14px" }}>
        Link sent.
      </h1>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16, maxWidth: "60ch", marginBottom: 28 }}>
        <div style={{ width: 44, height: 44, borderRadius: 999, background: "var(--ep-avocado)", color: "var(--ep-origin)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <IcCheck size={22} />
        </div>
        <p style={{ font: "400 17px/1.55 var(--ep-font-body)", color: "var(--ep-fg-muted)", margin: 0 }}>
          We emailed a secure link to <strong style={{ color: "var(--ep-origin)" }}>{match?.emailHint}</strong>.
          {/* "7 days" mirrors TOKEN_TTL_MIN in backend/routes/onboard.js — pinned
              together by onboarding-link-ttl.test.js. */}
          Open it to continue — it's valid for 7 days and can be opened 5 times, so a
          phone and a laptop both work.
        </p>
      </div>
      <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
        <button className="ep-btn secondary sm" onClick={onResend}>Resend link</button>
        <button className="ep-btn ghost sm" onClick={onReset}>Use a different record</button>
      </div>
      {error && <div style={{ marginTop: 14, fontSize: 13, color: "var(--ep-fg-muted)" }}>{error}</div>}
    </div>
  );
}
